import { defineStore } from 'pinia'
import { ref } from 'vue'
import { showToast } from 'vant'
import { useI18n } from 'vue-i18n'
import { EnumTaskAssetsType, EnumTaskStatus } from '@/enums/task'
import { apiCreateVideoTask, apiGetCreationTaskDetail, apiGetVideoTasks } from '@/apis/task'
import type { IWSTask } from '@/websocket'
import { onTextToVideoTaskStatusChanged } from '@/websocket/bus'
import {
  buildCreateVideoTaskPayload,
  buildRemoteCreationFiles,
  getCreationAssetLabel,
  normalizeDuration,
  normalizeForceTextToVideoType,
  normalizeRatio,
} from '@/utils/creation'
import type {
  CreationFile,
  CreationFormData,
  CreationTask,
  IReqCreateVideoTask,
  IResCreationTaskItem,
} from '@/types/apis/task'

const PAGE_SIZE = 20
const STATUS_DEDUP_WINDOW_MS = 1200
const TASK_STATUS_FILTERS = [
  EnumTaskStatus.WAITING,
  EnumTaskStatus.PADDING,
  EnumTaskStatus.PROGRESSING,
  EnumTaskStatus.SUCCESS,
  EnumTaskStatus.FAIL,
]

export const formatTaskJson = (task: IResCreationTaskItem): CreationTask => {
  const errorMessage = task.FormattedTaskMessage?.MessageCn || task.FormattedTaskMessage?.ErrorTypeDesc || undefined

  return {
    id: `task-${task.Id}`,
    taskId: task.Id,
    status: task.Status,
    CreateTime: task.CreateTime,
    prompt: task.Prompt,
    title: task.Title || task.Prompt,
    files: buildRemoteCreationFiles(task.Id, 'task', task),
    results: {
      videoUrl: task.SaveUrl || undefined,
    },
    errorMessage,
    forceTextToVideoType: normalizeForceTextToVideoType(task.TextToVideoType || 9),
    videoCategoryId: task.VideoCategoryId ?? undefined,
    ratio: normalizeRatio(task.AspectRatio),
    duration: task.LengthTime,
  }
}

const appendTask = (list: CreationTask[], task: CreationTask, prepend = false) => {
  if (prepend) {
    list.push(task)
    return
  }

  list.unshift(task)
}

export const useCreationStore = defineStore('creation', () => {
  const tasks = ref<CreationTask[]>([])
  const loading = ref(false)
  const loaded = ref(false)
  const hasMore = ref(true)
  const page = ref(1)
  const creating = ref(false)
  const realtimeSyncing = ref(false)
  const statusSyncCache = new Map<number, { status: EnumTaskStatus; timestamp: number }>()
  let taskStatusPushHandler: (() => unknown | Promise<unknown>) | null = null

  let taskStatusUnsubscribe: (() => void) | null = null

  // 新增或更新任务
  const upsertTask = (task: CreationTask, prepend = false) => {
    const index = tasks.value.findIndex(item => item.taskId === task.taskId)

    if (index >= 0) {
      tasks.value.splice(index, 1, task)
    } else {
      appendTask(tasks.value, task, prepend)
    }
  }

  const getNextFileLabel = (type: EnumTaskAssetsType, currentFiles: CreationFile[] = []): string => {
    const count = currentFiles.filter(file => file.type === type).length + 1
    return `${getCreationAssetLabel(type)}${count}`
  }

  const buildCreatePayload = (formData: CreationFormData): IReqCreateVideoTask => {
    return buildCreateVideoTaskPayload(formData)
  }

  const loadTasks = async (pageNum?: number) => {
    if (pageNum !== undefined) {
      page.value = pageNum
      if (pageNum === 1) {
        tasks.value = []
        hasMore.value = true
      }
    }

    if (loading.value || !hasMore.value) {
      return
    }

    try {
      loading.value = true
      const response = await apiGetVideoTasks({
        PageIndex: page.value,
        PageSize: PAGE_SIZE,
        StatusArray: TASK_STATUS_FILTERS,
      })
      response.Body.Items.forEach(item => upsertTask(formatTaskJson(item)))
      hasMore.value = response.Body.HasNextPage
      page.value += 1
      loaded.value = true
    } catch (error) {
      console.error('加载创作任务失败:', error)
      const { t } = useI18n()
      showToast(t('common.errors.loadFailed'))
    } finally {
      loading.value = false
    }
  }

  const appendTaskFromDetail = async (taskId: number) => {
    const response = await apiGetCreationTaskDetail({ Id: taskId })

    if (!response.IsSuccess || !response.Body) {
      return null
    }

    const task = formatTaskJson(response.Body)
    upsertTask(task, true)
    loaded.value = true
    return task
  }

  const createTask = async (formData: CreationFormData) => {
    if (creating.value) return

    creating.value = true

    try {
      const params = buildCreatePayload(formData)
      const response = await apiCreateVideoTask(params)

      return await appendTaskFromDetail(response.Body)
    } finally {
      creating.value = false
    }
  }

  const regenerateTask = async (task: CreationTask) => {
    return await createTask({
      prompt: task.prompt,
      files: task.files.map(file => ({ ...file })),
      ratio: task.ratio,
      duration: normalizeDuration(task.duration),
      forceTextToVideoType: task.forceTextToVideoType,
      videoCategoryId: task.videoCategoryId,
    })
  }

  const findTaskById = (taskId: number) => {
    return tasks.value.find(task => task.taskId === taskId) || null
  }

  const shouldSkipDuplicatedStatus = (taskId: number, status: EnumTaskStatus) => {
    const now = Date.now()
    const cache = statusSyncCache.get(taskId)

    if (cache && cache.status === status && now - cache.timestamp <= STATUS_DEDUP_WINDOW_MS) {
      return true
    }

    statusSyncCache.set(taskId, { status, timestamp: now })
    return false
  }

  const applyTaskStatus = (payload: IWSTask) => {
    const localTask = findTaskById(payload.taskId)

    if (!localTask) {
      return
    }

    const nextTask: CreationTask = {
      ...localTask,
      status: payload.status,
      errorMessage: payload.status === EnumTaskStatus.FAIL ? payload.failMsg : localTask.errorMessage,
    }

    upsertTask(nextTask)
  }

  const handleTaskStatusChanged = async (payload: IWSTask) => {
    if (shouldSkipDuplicatedStatus(payload.taskId, payload.status)) {
      return
    }

    if (payload.status === EnumTaskStatus.SUCCESS || payload.status === EnumTaskStatus.FAIL) {
      const task = await appendTaskFromDetail(payload.taskId)

      if (task) {
        return
      }
    }

    applyTaskStatus(payload)
  }

  const startRealtimeSync = () => {
    if (taskStatusUnsubscribe) {
      return
    }

    taskStatusUnsubscribe = onTextToVideoTaskStatusChanged(payload => {
      if (taskStatusPushHandler) {
        void taskStatusPushHandler()
      }
      void handleTaskStatusChanged(payload)
    })
    realtimeSyncing.value = true
  }

  const setTaskStatusPushHandler = (handler: (() => unknown | Promise<unknown>) | null) => {
    taskStatusPushHandler = handler
  }

  const stopRealtimeSync = () => {
    taskStatusUnsubscribe?.()
    taskStatusUnsubscribe = null
    realtimeSyncing.value = false
    statusSyncCache.clear()
  }

  const clear = () => {
    tasks.value = []
    page.value = 1
    hasMore.value = true
    loaded.value = false
    taskStatusPushHandler = null
    stopRealtimeSync()
  }

  return {
    tasks,
    loading,
    loaded,
    hasMore,
    page,
    creating,
    realtimeSyncing,
    loadTasks,
    createTask,
    regenerateTask,
    appendTaskFromDetail,
    setTaskStatusPushHandler,
    startRealtimeSync,
    stopRealtimeSync,
    getNextFileLabel,
    findTaskById,
    clear,
  }
})
